/* @jsx h */
import { defineComponent, h } from "vue";
import {
  SAMPLE_COIN_BALANCE,
  type WalletPassDesign,
  type WalletPassDesignDocument,
  type WalletStampOverlay,
} from "../../types";
import { PanelRow, PanelSection, ToggleRow } from "./primitives";

interface StampsPanelProps {
  design: WalletPassDesign;
  onChange: (patch: Partial<WalletPassDesignDocument>) => void;
}

/**
 * How coin stamps are drawn. The number of stamps is never a design setting:
 * it is the coin program's next reward threshold, so the card cannot drift
 * from what the till will honour.
 */
export const StampsPanel = defineComponent<StampsPanelProps>({
  name: "StampsPanel",
  props: ["design", "onChange"],
  setup(props) {
    const stamps = () => props.design.document.stamps!;
    const patch = (p: Partial<WalletStampOverlay>) =>
      props.onChange({ stamps: { ...stamps(), ...p } });

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection
          title="Coin stamps"
          description="Shows progress as stamps on the artwork: Apple strip and poster, Google hero, and the Collected card."
        >
          <ToggleRow
            label="Show stamps"
            value={stamps().enabled}
            onChange={(enabled) => patch({ enabled })}
          />

          {stamps().enabled && (
            <>
              <div class="text-xs text-base-content/60 bg-base-200/50 rounded-lg px-3 py-2">
                Slots follow the program's next reward; the preview uses a
                sample balance of {SAMPLE_COIN_BALANCE}.
              </div>

              <PanelRow label="Draw on (Apple)">
                <select
                  class="select select-bordered select-sm"
                  value={stamps().target}
                  onChange={(e) =>
                    patch({
                      target: (e.target as HTMLSelectElement)
                        .value as WalletStampOverlay["target"],
                    })
                  }
                >
                  <option value="strip">Strip image</option>
                  <option value="poster">Poster artwork</option>
                  <option value="both">Both</option>
                </select>
              </PanelRow>

              <PanelRow label="Alignment">
                <select
                  class="select select-bordered select-sm"
                  value={stamps().alignment}
                  onChange={(e) =>
                    patch({
                      alignment: (e.target as HTMLSelectElement)
                        .value as WalletStampOverlay["alignment"],
                    })
                  }
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </PanelRow>

              <PanelRow label="Layout">
                <select
                  class="select select-bordered select-sm"
                  value={stamps().arrangement}
                  onChange={(e) =>
                    patch({
                      arrangement: (e.target as HTMLSelectElement)
                        .value as WalletStampOverlay["arrangement"],
                    })
                  }
                >
                  <option value="row">Row</option>
                  <option value="grid">Grid</option>
                </select>
              </PanelRow>

              <PanelRow
                label="Max per row"
                hint="Stamps wrap to a new row past this count."
              >
                <input
                  type="number"
                  min="1"
                  max="20"
                  class="input input-bordered input-sm"
                  value={stamps().max_per_row}
                  onInput={(e) =>
                    patch({
                      max_per_row: Math.max(
                        1,
                        Math.min(
                          20,
                          Number((e.target as HTMLInputElement).value) || 1,
                        ),
                      ),
                    })
                  }
                />
              </PanelRow>

              <PanelRow
                label="When the balance exceeds the card"
                hint="Cap is safest — it never reads as 'not ready yet' when a reward is owed."
              >
                <select
                  class="select select-bordered select-sm"
                  value={stamps().overflow}
                  onChange={(e) =>
                    patch({
                      overflow: (e.target as HTMLSelectElement)
                        .value as WalletStampOverlay["overflow"],
                    })
                  }
                >
                  <option value="cap">Cap — fill every slot and stop</option>
                  <option value="wrap">Wrap — restart, badge completions</option>
                  <option value="extend">Extend — draw extra rows</option>
                </select>
              </PanelRow>

              <PanelRow
                label={`Scrim opacity — ${Math.round(stamps().scrim_opacity * 100)}%`}
                hint="Darkens the artwork so stamps stay legible over a photo."
              >
                <input
                  type="range"
                  min="0"
                  max="100"
                  class="range range-sm range-primary"
                  value={stamps().scrim_opacity * 100}
                  onInput={(e) =>
                    patch({
                      scrim_opacity:
                        Number((e.target as HTMLInputElement).value) / 100,
                    })
                  }
                />
              </PanelRow>

              <ToggleRow
                label="Show caption"
                hint="Renders the stamp count into the Apple strip image."
                value={stamps().caption_enabled}
                onChange={(caption_enabled) => patch({ caption_enabled })}
              />
            </>
          )}
        </PanelSection>
      </div>
    );
  },
});
